import { useConfig } from '@exam-center/configuration';
import {
  type QuestionnaireTemplateId,
  useExportQuestionnairesLazyQuery,
  useCountExamEventsWithQuestionnairesQuery,
} from '@exam-center/graphql';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Text,
  Link,
  type DialogProps,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { dayjs } from '@shared/utils';

import { QUESTIONNAIRE_TEMPLATE_LABEL_MAP } from '../../constants';
import { useQuestionnaireExportSearchParams } from '../../hooks';

type QuestionnaireExportConfirmationModalProps = Pick<DialogProps, 'open'> & {
  onClose: () => void;
};

export default function QuestionnaireExportConfirmationModal(
  props: Readonly<QuestionnaireExportConfirmationModalProps>,
) {
  const { open, onClose } = props;

  const { showSnackbar } = useSnackbar();
  const configuration = useConfig();

  const dataDictionaryUrl = configuration.questionnaire.dataDictionaryUrl;

  const [{ examCenters, startDate, endDate, questionnaireTemplate, examCourses }] =
    useQuestionnaireExportSearchParams();

  const startDateFormatted = startDate ? dayjs(startDate) : null;
  const endDateFormatted = endDate ? dayjs(endDate) : null;

  const { data: countData } = useCountExamEventsWithQuestionnairesQuery({
    variables: {
      countExamEventsWithQuestionnairesInput: {
        examCenterCodes: examCenters.includes('all') ? null : examCenters,
        startDate: startDateFormatted?.startOf('day').toDate() ?? new Date(0),
        endDate: endDateFormatted?.endOf('day').toDate() ?? new Date(0),
        examCourseCodes: examCourses.includes('all') ? null : examCourses,
        templateId: questionnaireTemplate as QuestionnaireTemplateId,
      },
    },
    skip:
      examCenters.length === 0 ||
      examCourses.length === 0 ||
      !startDate ||
      !endDate ||
      !questionnaireTemplate,
    fetchPolicy: 'cache-first',
  });

  const total = countData?.ecCountExamEventsWithQuestionnaires.total ?? 0;
  const isExceedLimit = total >= 50_000;

  const examDateDisplay =
    startDateFormatted && endDateFormatted
      ? `${startDateFormatted.format('DD-MM-YYYY')} to ${endDateFormatted.format('DD-MM-YYYY')}`
      : '';

  const templateDisplay = questionnaireTemplate
    ? QUESTIONNAIRE_TEMPLATE_LABEL_MAP[questionnaireTemplate as QuestionnaireTemplateId]
    : '';

  const examCenterDisplay = examCenters?.includes('all') ? 'Select All' : examCenters.join(', ');

  const examCourseDisplay = examCourses?.includes('all') ? 'Select All' : examCourses.join(', ');

  const [exportQuestionnaires, { loading: isExportingQuestionnaires }] =
    useExportQuestionnairesLazyQuery({
      fetchPolicy: 'no-cache',
    });

  const handleExport = async () => {
    await exportQuestionnaires({
      variables: {
        exportQuestionnairesInput: {
          examCenterCodes: examCenters.includes('all') ? null : examCenters,
          startDate: startDateFormatted?.startOf('day').toDate() ?? new Date(0),
          endDate: endDateFormatted?.endOf('day').toDate() ?? new Date(0),
          examCourseCodes: examCourses.includes('all') ? null : examCourses,
          templateId: questionnaireTemplate as QuestionnaireTemplateId,
        },
      },
      onCompleted: () => {
        showSnackbar({
          message: 'ส่งออกข้อมูลเรียบร้อยแล้ว',
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
          variant: 'error',
        });
        onClose();
      },
    });
  };

  return (
    <Dialog open={open} disableEscapeKeyDown={isExportingQuestionnaires} maxWidth='xs'>
      <DialogTitle onClose={onClose} loading={isExportingQuestionnaires}>
        ยืนยันการส่งออกข้อมูล
      </DialogTitle>
      <DialogContent dividers>
        <Flex sx={(theme) => ({ gap: theme.spacing(4), flexDirection: 'column' })}>
          {isExceedLimit ? (
            <Alert severity='error' sx={{ fontWeight: 400 }}>
              จำกัดการดาวน์โหลดสูงสุด 50,000 รายการต่อครั้ง
            </Alert>
          ) : null}
          <Flex sx={(theme) => ({ gap: theme.spacing(4), flexDirection: 'column' })}>
            <Flex sx={(theme) => ({ gap: theme.spacing(3), flexDirection: 'column' })}>
              <Text variant='body1Semibold'>สรุปข้อมูลที่เลือก</Text>

              <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
                <Text sx={{ flexBasis: '100px', flexShrink: 0 }} variant='body1Semibold'>
                  Exam Date:
                </Text>
                <Text>{examDateDisplay}</Text>
              </Flex>

              <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
                <Text sx={{ flexBasis: '100px', flexShrink: 0 }} variant='body1Semibold'>
                  Exam Center:
                </Text>
                <Text>{examCenterDisplay}</Text>
              </Flex>

              <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
                <Text sx={{ flexBasis: '100px', flexShrink: 0 }} variant='body1Semibold'>
                  Template:
                </Text>
                <Text>{templateDisplay}</Text>
              </Flex>

              <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
                <Text sx={{ flexBasis: '100px', flexShrink: 0 }} variant='body1Semibold'>
                  Exam Course:
                </Text>
                <Text>{examCourseDisplay}</Text>
              </Flex>

              <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
                <Text sx={{ flexBasis: '100px', flexShrink: 0 }} variant='body1Semibold'>
                  รวมทั้งหมด:
                </Text>
                <Text>{total} รายการ</Text>
              </Flex>
            </Flex>
            <Text
              variant='body2'
              sx={(theme) => ({
                color: theme.palette.text.secondary,
              })}
            >
              คำอธิบายความหมายของคอลัมน์ในไฟล์นี้{' '}
              <Link
                color='info'
                rel='noreferrer'
                sx={{ cursor: 'pointer' }}
                target='_blank'
                href={dataDictionaryUrl}
              >
                ดาวน์โหลด Data Dictionary
              </Link>
            </Text>
          </Flex>
        </Flex>
      </DialogContent>
      <DialogActions>
        <Button size='large' variant='outlined' color='secondary' onClick={onClose}>
          ยกเลิก
        </Button>
        <Button
          size='large'
          color='primary'
          onClick={handleExport}
          loading={isExportingQuestionnaires}
          disabled={isExportingQuestionnaires || isExceedLimit}
        >
          ส่งออกข้อมูล
        </Button>
      </DialogActions>
    </Dialog>
  );
}
